<script>
    import Banner from '@itslearning/prometheus/assets/feedback/Banner/v1/Banner.svelte'
    import Button from '@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte';
    import IconButton from '@itslearning/prometheus/assets/inputs/IconButton/v1/IconButton.svelte';
    import Link from '@itslearning/prometheus/assets/Navigation/Link/v1/Link.svelte';
    import Modal from '@itslearning/prometheus/assets/modals/Modal/v2/Modal.svelte';
    import Page from './Page/Page.svelte';
    import Question from './Question/Question.svelte';
    import {questions, questionSequence, currentQuestionIndex, questionAnswers, survey, isInActiveForRespondents, hasDeadlinePassedForRespondents, 
        permissions, surveyTitle, isPreviewMode, resetSurveyAnswer, navigation} from '../stores';
    import {navigateToCompletedSurveyPage, navigateToQuestionListPage, navigateToEditQuestionPage, navigateToViewSurveyPage} from '../navigation';
    import {txt} from '../i18n/i18n';
    import {saveQuestionAnswer, deleteRespondents} from '../api';
    import { onMount } from 'svelte';
    import {isInIframe} from '../helpers';
    import ProgressBar from "./ProgressBar/ProgressBar.svelte";

    let clearAnswerText = '';
    let message = { text: txt('Unknown_error'), type: 'warning' };
    let isSavingPrevious = false;
    let isSaving = false;
    let showErrorMessage = false;
    let isDeleting = false;
    let confirmVisible = false;
    let heading;
    let focusHeading = true;

    $: questionId = $questionSequence[$currentQuestionIndex];
    $: question = $questions.find(q => q.questionId === questionId);
    $: questionNumber = $currentQuestionIndex + 1;
    $: currentAnswer = $questionAnswers.find(a => a.questionId === questionId);
    $: answerId = $survey.answerId;
    $: showEditQuestionButton =  $permissions.canModify && isPreviewMode() && !$navigation.hideEditButton;
    $: shouldHaveClearAnswerButton = question.type === 'YesNo' || question.type === 'Matrix' || (question.type === 'MultipleChoice' && question.maxAlternativesToSelect === 1);   
    $: if (focusHeading && heading) {
        heading.focus();
        focusHeading = false;
    }

    onMount(async () => {
        if (isPreviewMode()) {
            return; // we just continue on the question in the session
        }
        
        // Continue where user left off
        for (let i=0; i < $questionSequence.length; i++) {
            let questionId = $questionSequence[i];
            let answer = $questionAnswers.find(a => a.questionId === questionId);
            if (!answer || !answer.isAnswered()) {
                $currentQuestionIndex = i;
                break;
            }
        }
    });

    async function previousQuestion() {
        if (!isPreviewMode()) {
            try {
                if (isSaving || isSavingPrevious) {
                    return;
                }
                isSavingPrevious = true;
                await saveQuestionAnswer(answerId, currentAnswer);
            } catch {
                showErrorMessage = true;
                isSavingPrevious = false;
                return;
            }
            showErrorMessage = false;
        }

        clearAnswerText = '';
        $currentQuestionIndex = $currentQuestionIndex - 1;
        focusHeading = true;
        isSavingPrevious = false;
    }

    async function nextQuestion() {
        if (!isPreviewMode()) {
            try {
                if (isSaving || isSavingPrevious) {
                    return;
                }
                isSaving = true;
                await saveQuestionAnswer(answerId, currentAnswer);
            } catch {
                showErrorMessage = true;
                isSaving = false;
                return;
            }
            showErrorMessage = false;
        }

        clearAnswerText = '';
        $currentQuestionIndex = $currentQuestionIndex + 1;
        focusHeading = true;
        isSaving = false;
    }

    async function submitAnswer() {
        if (isPreviewMode()) {
            completePreviewAndNavigateToQuestionList();
        } else {
            try {
                if (isSaving || isSavingPrevious) {
                    return;
                }
                isSaving = true;
                await saveQuestionAnswer(answerId, currentAnswer, true);
                survey.update(currentSurvey => {
                    currentSurvey.completed = new Date().toISOString();
                    return currentSurvey;
                });
                $currentQuestionIndex = 0;
                $questionAnswers = [];
            } catch {
                showErrorMessage = true;
                isSaving = false;
                return;
            } 

            showErrorMessage = false;
            navigateToCompletedSurveyPage();
            isSaving = false;
        }
    }
    
    function completePreviewAndNavigateToQuestionList() {
        $currentQuestionIndex = 0;
        $questionAnswers = [];
        navigateToQuestionListPage();
    }
   
    function clearAnswer() {
        $questionAnswers = $questionAnswers.map(a => {
            return a.questionId === question.questionId ? 
                createEmptyAnswer(question)
                : a
            });
        clearAnswerText = txt('Answer_was_cleared');
        setTimeout(() => { clearAnswerText = ''; }, 1000);
    }

    function confirmExit(){
        confirmVisible = true;
    }

    async function cancelAnswer() {
        try {
            if(isDeleting) {
                return;
            }
            isDeleting = true;
            await deleteRespondents([answerId]);
        } catch {
            showErrorMessage = true;
            return;
        } finally {
            isDeleting = false;
        }
        navigateToViewSurveyPage();
        resetSurveyAnswer();
    }

    function resetCancel() {
        confirmVisible = false;
    }

    function createEmptyAnswer(question) {
        const ans = {questionId: question.questionId};
        if (question.type === 'YesNo') {
            ans.answer = '';
        } else if (question.type === 'Open') {
            ans.text = '';
        } else if (question.type === 'Matrix') {
            ans.matrixAnswers = [];
            question.matrixRows.forEach(r => {
                ans.matrixAnswers.push({matrixRowId: r.matrixRowId, alternativeIds: []});
            });
        } else {
            ans.alternativeIds = [];
        }
        return ans;
    }
</script>

<Page title={!isInIframe() ? surveyTitle : txt('Take_survey')} displayHeading={!isInIframe()} headingLevel={1}>
    <Banner visible={showErrorMessage} close={txt('Close')} type={message.type} text={message.text}></Banner>
    <form on:submit|preventDefault id={"form" + question.questionId} tabindex="-1" class="proto-form">
        <Modal enabled={confirmVisible}
               titleText={txt('Exit_survey')}
               bodyText={txt('Your_response_will_not_be_saved')}
               cancelText={txt('Cancel')}
               confirmText={txt('Exit_survey_button')}
               on:confirm={cancelAnswer}
               on:cancel={resetCancel}
        />
        
        {#if isPreviewMode()}
            <div>
                <Link kind="standalone" iconClass="back-link" href="#questions" on:click={completePreviewAndNavigateToQuestionList} text="{txt('Back_to_list_of_questions')}" />
            </div>
        {/if}

        <div class="header-progress-bar">
            <h2 bind:this={heading} tabindex="-1" class="question-heading">{txt('Question_x_of_y', [questionNumber, $questions.length])}</h2>
            <ProgressBar fromPercent="{((questionNumber - 1 * 100) / $questions.length)}" percent="{((questionNumber * 100) / $questions.length)}" />
        </div>

        <Question {question} />

        {#if shouldHaveClearAnswerButton}
            <div>
                <IconButton text={txt('Clear_answer')} htmlType="button" type="plain" iconClass="clear-answer" on:click={clearAnswer}></IconButton>
                <div aria-live="polite" class="screen-reader">{clearAnswerText}</div>
            </div>
        {/if}
        <div class="footer-actions">
            <div class="footer-actions-left">
                <div class="footer-actions-reverse">
                    <Button text={txt('Previous')} htmlType="button" type="secondary" disabled={$currentQuestionIndex === 0 || $isInActiveForRespondents || $hasDeadlinePassedForRespondents} on:click={previousQuestion} loading={isSavingPrevious} />
                    {#if $currentQuestionIndex < $questionSequence.length - 1}
                        <Button text={txt('Next')} htmlType="button" type="primary" disabled={$isInActiveForRespondents || $hasDeadlinePassedForRespondents} on:click={nextQuestion} loading={isSaving} />
                    {:else if isPreviewMode()}
                        <Button text={txt('Exit_preview')} htmlType="button" type="primary" on:click={submitAnswer} />
                    {:else }
                        <Button text={txt('Submit_answer')} htmlType="button" type="primary" disabled={$isInActiveForRespondents || $hasDeadlinePassedForRespondents} on:click={submitAnswer} loading={isSaving} />
                    {/if}   
                </div>           
                {#if showEditQuestionButton}
                    <Link kind="standalone" iconClass="edit-link" text={txt('Edit_question')} on:click={() => navigateToEditQuestionPage(question.type, question.questionId)} />
                {/if}
            </div>
            {#if $permissions.canModify && !isPreviewMode()}
                <div class="footer-actions-right">
                    <Button type="plain" htmlType="button" text={txt('Cancel')} on:click={confirmExit} loading={isDeleting}/>
                </div>
            {/if}
        </div>
    </form>
</Page>

